/**
 * @file 教师新建 / 编辑弹窗
 * @description 名称、账号、科目（多选）、密码（可空默认 123456）、状态。
 *
 * @whenToUse 教师管理页创建与编辑。
 * @whenNotToUse 其他业务表单弹窗。
 */

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { Input } from '@/components/ui/input.tsx'
import { MultiSelect } from '@/components/ui/multi-select.tsx'
import { Select } from '@/components/ui/select.tsx'
import {
  EMPTY_TEACHER_FORM,
  SUBJECT_OPTIONS,
  type TeacherFormValues,
  type TeacherRow,
} from '@/components/pages/teacher-page/mock.ts'

/** 登录账号：字母 / 数字 / 下划线 */
const ACCOUNT_PATTERN = /^[a-zA-Z0-9_]+$/

const STATUS_OPTIONS = [
  { value: '1', label: '启用' },
  { value: '0', label: '禁用' },
]

/**
 * 过滤账号非法字符
 * @param value 原始输入
 */
function sanitizeAccount(value: string): string {
  return value.replace(/[^a-zA-Z0-9_]/g, '')
}

export type TeacherFormDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 编辑目标；为空表示新建 */
  editing: TeacherRow | null
  onSubmit: (values: TeacherFormValues) => void
}

/**
 * 教师创建 / 编辑 Dialog
 */
export function TeacherFormDialog({
  open,
  onOpenChange,
  editing,
  onSubmit,
}: TeacherFormDialogProps) {
  const isEdit = Boolean(editing)
  const [values, setValues] = useState<TeacherFormValues>(EMPTY_TEACHER_FORM)
  const [errors, setErrors] = useState<
    Partial<Record<keyof TeacherFormValues, string>>
  >({})

  useEffect(() => {
    if (!open) return
    if (editing) {
      setValues({
        name: editing.name,
        account: editing.account,
        subjects: [...editing.subjects],
        password: '',
        enabled: editing.enabled,
      })
    } else {
      setValues(EMPTY_TEACHER_FORM)
    }
    setErrors({})
  }, [open, editing])

  const setField = <K extends keyof TeacherFormValues>(
    key: K,
    value: TeacherFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const validate = () => {
    const next: Partial<Record<keyof TeacherFormValues, string>> = {}
    if (!values.name.trim()) next.name = '请输入教师名称'
    const account = values.account.trim()
    if (!account) next.account = '请输入教师账号'
    else if (!ACCOUNT_PATTERN.test(account))
      next.account = '仅支持字母、数字和下划线'
    if (values.subjects.length === 0) next.subjects = '请选择科目'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = () => {
    if (!validate()) return
    onSubmit(values)
    onOpenChange(false)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? '编辑教师' : '新增教师'}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button onClick={handleSubmit}>{isEdit ? '保存' : '创建'}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField label="教师名称" required error={errors.name}>
          <Input
            value={values.name}
            invalid={Boolean(errors.name)}
            placeholder="请输入教师名称"
            onChange={(event) => setField('name', event.target.value)}
          />
        </FormField>
        <FormField
          label="教师账号"
          required
          error={errors.account}
          hint="仅支持字母、数字和下划线"
        >
          <Input
            value={values.account}
            invalid={Boolean(errors.account)}
            placeholder="如 wang_teacher"
            autoComplete="username"
            onChange={(event) =>
              setField('account', sanitizeAccount(event.target.value))
            }
          />
        </FormField>
        <FormField label="科目" required error={errors.subjects}>
          <MultiSelect
            value={values.subjects}
            options={SUBJECT_OPTIONS}
            placeholder="请选择科目（可多选）"
            maxTagCount={3}
            invalid={Boolean(errors.subjects)}
            onChange={(value) => setField('subjects', value)}
          />
        </FormField>
        <FormField
          label="教师密码"
          error={errors.password}
          hint={isEdit ? '留空表示不修改密码' : '非必填，留空则默认 123456'}
        >
          <Input
            type="password"
            value={values.password}
            invalid={Boolean(errors.password)}
            placeholder={isEdit ? '不修改请留空' : '请输入密码（默认123456）'}
            autoComplete="new-password"
            onChange={(event) => setField('password', event.target.value)}
          />
        </FormField>
        <FormField label="教师状态" required>
          <Select
            value={values.enabled ? '1' : '0'}
            options={STATUS_OPTIONS}
            onChange={(value) => setField('enabled', value === '1')}
          />
        </FormField>
      </div>
    </Dialog>
  )
}
